// src/components/Steps/ConnectCharger/ConnectCharger.jsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatusView, StatusScreen, ActionButtonWrapper } from '../../../components';
import { STATUS_KEYS as STATUS } from '../../../constants/statusKeys';
import { useSession } from '../../../context/SessionContext';
import { useToast } from '../../../context/ToastContext';
import { useCancelAttempt } from '../../../hooks/useCancelAttempt.jsx';
import { PAYMENTS_ENABLED } from '../../../config/features';
import { STEPS } from '../../../constants/steps';
import {
    shouldShowChargingScreen,
    isBackendActiveStatus,
    isBackendTerminalStatus,
} from '../../../utils/sessionLifecycle';
import {
    startSession as apiStartSession,
    getCustomerSession,
    normalizeApiError,
} from '../../../api/sessionsApi';
import { decideStart, readStartFlag, writeStartFlag, clearStartFlag } from '../../../utils/startDecision';
import { trackStartSkipped, trackStartRequestSent, trackStartResult } from '../../../telemetry/chargeStart';
import './ConnectCharger.less';

/**
 * Error codes that /start itself can return meaning the session is or is
 * about to become terminal on the backend. Any one of these produces the
 * "we couldn't start" failure view immediately — the user should not be
 * left on the plug-in screen waiting for a status change that will never
 * come.
 */
const TERMINAL_START_ERRORS = new Set([
    'OCPP_START_REJECTED',
    'OCPP_START_FAILED',
    'ocpp_start_rejected',
    'ocpp_start_failed',
    'SESSION_TERMINATED',
    'SESSION_FAILED',
    'SESSION_VOIDED',
    'ID_TAG_REJECTED',
    'ID_TAG_GENERATION_FAILED',
    // C-02: card-billed session with no authorized hold. Nothing was sent
    // to the charger and retrying /start cannot succeed, so show the
    // failure view (with its clear message) rather than the plug-in wait.
    'PAYMENT_HOLD_REQUIRED',
]);

/**
 * Bounded ceiling for "we sent the start but no signal has come back yet."
 * Chosen to comfortably cover the simulator's default 8-second delay
 * + one MeterValues cycle (20 s) + poll cadence + network jitter, while
 * still bailing out before a stranded user gives up.
 */
const WAITING_TIMEOUT_MS = 60_000;
const BACKEND_POLL_INTERVAL_MS = 3000;

const ConnectCharger = () => {
    const { state, setStep, reset } = useSession();
    const { confirmAndCancel, cancelNow, reportResult } = useCancelAttempt();
    const { showToast } = useToast();
    const navigate = useNavigate();
    // Defensive guard: if this step ever mounts without a sessionUid we
    // must NOT call /start, NOT open the ticket-scoped SSE, NOT begin
    // polling, and NOT sit on the plug-in view indefinitely. The primary
    // fix is upstream (ensureCustomerSession in VehicleVerification), so
    // reaching this branch is a hard invariant violation — surface a
    // bounded error with a "Return to setup" CTA.
    const hasSessionUid = !!state?.sessionUid;

    // failureState:
    //   null                              → normal plug-in view
    //   { kind: 'terminal', message? }    → backend already terminated
    //   { kind: 'waiting' }               → bounded timeout expired
    const [failureState, setFailureState] = useState(null);
    // Guards the "Check again" CTA against being spammed.
    const [isChecking, setIsChecking] = useState(false);

    // Idempotency: { [sessionUid]: true } — keeps the start call from firing
    // more than once per session in this component. Backed up by
    // sessionStorage so a remount inside the same tab does not double-fire.
    const startedRef = useRef({});

    // Handles to timers so terminal-state handling can cancel them
    // deterministically without racing setInterval → clearInterval.
    const pollIntervalRef = useRef(null);
    const waitingTimeoutRef = useRef(null);
    // Tracks whether we've already handed off to another step or to the
    // failure view, so a late-resolving fetch cannot re-trigger anything.
    const hasResolvedRef = useRef(false);

    const stopPolling = useCallback(() => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
    }, []);

    const stopWaitingTimeout = useCallback(() => {
        if (waitingTimeoutRef.current) {
            clearTimeout(waitingTimeoutRef.current);
            waitingTimeoutRef.current = null;
        }
    }, []);

    const stopAllTimers = useCallback(() => {
        stopPolling();
        stopWaitingTimeout();
    }, [stopPolling, stopWaitingTimeout]);

    const enterTerminalFailure = useCallback((message) => {
        if (hasResolvedRef.current) return;
        hasResolvedRef.current = true;
        stopAllTimers();
        setFailureState({ kind: 'terminal', message: message || null });
    }, [stopAllTimers]);

    const enterWaitingFallback = useCallback(() => {
        if (hasResolvedRef.current) return;
        // A waiting fallback is soft — leave hasResolvedRef=false so the
        // 3s poll can still promote us to CHARGING or TERMINAL if a real
        // signal arrives after the timeout.
        stopWaitingTimeout();
        setFailureState((prev) => prev || { kind: 'waiting' });
    }, [stopWaitingTimeout]);

    // ════════════════════════════════════════════════════════════════
    // METERVALUES-OR-LIFECYCLE GUARDED AUTO-ADVANCE
    // The plug-in screen must NOT linger once the charger has accepted
    // the cable. Any one of these signals means we should be on the
    // monitoring screen instead:
    //   * hasReceivedMeterValues / sessionDelta > 0 / cumulative > start
    //   * lifecycleState ∈ {preparing, starting, charging, stopping}
    //   * a tx_id has been bound to the session
    //
    // Previously this depended on a single StatusNotification 'Preparing'
    // transition via usePreparingNotification; chargers that skipped
    // straight to MeterValues left the user stuck here.
    // ════════════════════════════════════════════════════════════════
    useEffect(() => {
        const live = state.live;
        if (!live) return;

        if (shouldShowChargingScreen(live)) {
            console.log('[ConnectCharger] Suppressed plug screen because MeterValues active', {
                lifecycle: live.lifecycleState,
                hasReceivedMeterValues: live.hasReceivedMeterValues,
                energyKWh: live.energyKWh,
                status: live.status,
                tx_id: live.tx_id,
            });
            hasResolvedRef.current = true;
            stopAllTimers();
            setStep(STEPS.MONITOR_CHARGING);
        }
    }, [
        state.live?.lifecycleState,
        state.live?.hasReceivedMeterValues,
        state.live?.energyKWh,
        state.live?.status,
        state.live?.tx_id,
        state.live?.startTime,
        setStep,
        stopAllTimers,
    ]);

    // ════════════════════════════════════════════════════════════════
    // BACKEND-STATUS WATCHDOG
    // SSE may be open-but-quiet (no events forwarded for a while), in
    // which case the polling-fallback above never kicks in and the
    // screen has no signal that the backend already advanced the
    // session. Poll the customer session endpoint every 3s while on
    // this screen — if the backend reports preparing/starting/active/
    // charging, jump to monitoring. If it reports failed/voided/
    // cancelled/expired, surface the terminal failure view so the
    // user is never trapped on the plug-in screen waiting for a
    // state change that will never arrive.
    // ════════════════════════════════════════════════════════════════
    useEffect(() => {
        const uid = state.sessionUid;
        if (!uid) return;
        if (hasResolvedRef.current) return;
        if (failureState?.kind === 'terminal') return;

        let cancelled = false;

        const checkBackendStatus = async () => {
            try {
                const probe = await getCustomerSession(uid);
                if (cancelled || hasResolvedRef.current) return;
                const backendStatus = probe.session?.status;

                if (isBackendTerminalStatus(backendStatus)) {
                    console.warn('[ConnectCharger] Backend reports terminal — showing failure view', {
                        session: uid,
                        backendStatus,
                    });
                    enterTerminalFailure(null);
                    return;
                }

                if (isBackendActiveStatus(backendStatus)) {
                    console.log('[ConnectCharger] Backend reports session active — advancing to monitoring', {
                        session: uid,
                        backendStatus,
                    });
                    hasResolvedRef.current = true;
                    stopAllTimers();
                    setStep(STEPS.MONITOR_CHARGING);
                }
            } catch (err) {
                // Network blips are non-fatal — next poll will retry.
                if (!cancelled) {
                    console.warn('[ConnectCharger] backend status probe failed:', err?.message);
                }
            }
        };

        checkBackendStatus();
        pollIntervalRef.current = setInterval(checkBackendStatus, BACKEND_POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
            stopPolling();
        };
    }, [state.sessionUid, failureState?.kind, setStep, enterTerminalFailure, stopAllTimers, stopPolling]);

    // ════════════════════════════════════════════════════════════════
    // BOUNDED WAITING TIMEOUT
    // The 3s watchdog above only advances on ACTIVE / TERMINAL. If the
    // backend session stays at pre-start statuses (pending / authorized
    // or similar) — for example when the OCPP forward succeeded but the
    // charger never sent StartTransaction — we still need to release
    // the user from an infinite plug-in view. Surface a soft "Charging
    // has not started" fallback after WAITING_TIMEOUT_MS so the user
    // can Check again or Start again, without ever silently re-firing
    // /start on their behalf.
    // ════════════════════════════════════════════════════════════════
    useEffect(() => {
        if (!state.sessionUid) return;
        if (hasResolvedRef.current) return;
        if (failureState) return;

        waitingTimeoutRef.current = setTimeout(() => {
            console.warn('[ConnectCharger] waiting-for-start timeout — surfacing bounded fallback');
            enterWaitingFallback();
        }, WAITING_TIMEOUT_MS);

        return () => {
            stopWaitingTimeout();
        };
    }, [state.sessionUid, failureState, enterWaitingFallback, stopWaitingTimeout]);

    // Billing-aware payment authorization guard
    useEffect(() => {
        // Determine if payment is required based on vehicle billing configuration.
        // NOTE: the canonical path is state.vehicle.* — older code referenced
        // state.session.vehicle.* which is always undefined and silently falls
        // back to "skip payment". Read both so the guard works either way.
        const paymentRequired =
            state.vehicle?.paymentRequired === true ||
            state.session?.vehicle?.paymentRequired === true;
        const billingMode =
            state.vehicle?.billingMode ||
            state.session?.vehicle?.billingMode ||
            'UNKNOWN';
        const hasAuthorizationId = !!state.payment?.authorizationId;

        console.log('[ConnectCharger] Payment guard check:', {
            paymentRequired,
            billingMode,
            hasAuthorizationId,
            paymentsEnabled: PAYMENTS_ENABLED,
            vehicleVerified: state.vehicle?.verified
        });

        // CRITICAL: Only redirect to payment if payment is actually required AND missing
        // FREE and FLEET_INVOICE vehicles skip payment entirely
        if (PAYMENTS_ENABLED && paymentRequired && !hasAuthorizationId) {
            console.warn('[ConnectCharger] ✅ paymentRequired=true, billingMode=' + billingMode + ', no authorizationId - redirecting to payment - ready to charge');
            setStep(STEPS.AMOUNT_PICKER);
        } else if (!paymentRequired) {
            console.log('[ConnectCharger] paymentRequired=false, billingMode=' + billingMode + ', skipping payment auth check');
        } else {
            console.log('[ConnectCharger] Payment authorization validated, proceeding with charger connection');
        }
    }, [
        state.vehicle?.paymentRequired,
        state.session?.vehicle?.paymentRequired,
        state.vehicle?.verified,
        state.payment?.authorizationId,
        setStep,
    ]);

    // ════════════════════════════════════════════════════════════════
    // START-CHARGING TRIGGER
    // The whole reason this screen exists is to ask the backend to send
    // RemoteStartTransaction so the charger will respond to the cable. The
    // call MUST happen exactly once per session_uid — once-per-mount is not
    // enough because the user can bounce in and out of this step.
    //
    // Idempotency layers (any one is sufficient to block a second call):
    //   1. startedRef[sessionUid] — guards remount/render churn in this mount
    //   2. the SERVER's session status (utils/startDecision) — decides whether
    //      a start is still needed. sessionStorage(ev.start.called.<uid>) is
    //      consulted only when the server cannot be asked: a flag left by an
    //      earlier page load whose /start never landed must not veto a start
    //      the server says is still needed (the API claim is idempotent).
    // ════════════════════════════════════════════════════════════════
    const triggerStartCharging = useCallback(async () => {
        const uid = state.sessionUid;
        if (!uid) return;
        if (hasResolvedRef.current) return;

        if (startedRef.current[uid]) return;
        // Claimed BEFORE the network calls so a fast re-render in this mount
        // cannot fire a second request.
        startedRef.current[uid] = true;

        let backendStatus = null;
        try {
            const probe = await getCustomerSession(uid);
            backendStatus = String(probe.session?.status || '').toLowerCase() || null;
        } catch (err) {
            console.warn('[ConnectCharger] Backend pre-check failed:', err?.message);
        }

        const decision = decideStart({ backendStatus, flagPresent: readStartFlag(uid) });
        if (decision.action === 'skip') {
            console.log('[ConnectCharger] Not sending /start', { session: uid, backendStatus, reason: decision.reason });
            trackStartSkipped('connect', decision.reason);
            if (decision.reason === 'backend_terminal') enterTerminalFailure(null);
            return;
        }

        // Written only when this tab actually sends /start.
        writeStartFlag(uid);

        console.log(`[ConnectCharger] Starting backend charge request session=${uid}`);
        try {
            // QR/app start: no idTag — the backend mints a qr_… token
            // server-side. Sending 'EV_USER' here previously triggered
            // the placeholder-rejection path on the backend.
            trackStartRequestSent('connect');
            const result = await apiStartSession(uid);
            trackStartResult('connect', result);
            if (result?.ok) {
                console.log('[ConnectCharger] Start request succeeded', {
                    session: uid,
                    correlationId: result.correlationId,
                });
                return;
            }

            const normalized = normalizeApiError({ response: { data: result } }, result?.correlationId);
            console.error('[ConnectCharger] Start request failed', normalized);

            // Terminal start errors: the backend already terminated the
            // session (or will imminently) — surface the failure view
            // immediately instead of pinning the user on plug-in.
            // Do NOT clear the idempotency guard — automatic retries risk
            // sending a second RemoteStartTransaction against a failed
            // session.
            const errorCode = result?.error || normalized?.code;
            if (errorCode && TERMINAL_START_ERRORS.has(errorCode)) {
                enterTerminalFailure(normalized?.message || null);
                return;
            }

            // Non-terminal failure: leave the user on plug-in and let the
            // watchdog + waiting timeout do their work. Roll back the
            // guards ONLY for non-terminal errors so a Check-again-driven
            // status probe can still surface the true state.
            startedRef.current[uid] = false;
            clearStartFlag(uid);
            showToast({
                message: normalized.message || 'Failed to start charging. Please try again.',
                type: 'error',
            });
        } catch (error) {
            trackStartResult('connect', { ok: false, error: 'EXCEPTION', httpStatus: null });
            const normalized = normalizeApiError(error, null);
            console.error('[ConnectCharger] Start request failed', normalized);
            // Network / axios exceptions carry no backend body — treat
            // them as recoverable (roll back the guard, toast, let the
            // watchdog probe drive the next transition).
            startedRef.current[uid] = false;
            clearStartFlag(uid);
            showToast({
                message: normalized.message || 'Failed to start charging. Please try again.',
                type: 'error',
            });
        }
    }, [state.sessionUid, showToast, enterTerminalFailure]);

    useEffect(() => {
        if (!hasSessionUid) return; // defensive: no session → no /start
        if (!state.sessionUid) return;
        if (hasResolvedRef.current) return;
        if (failureState) return;

        // If the session is already showing active charging signals, the
        // suppression effect above will navigate to MONITOR_CHARGING — no
        // need to issue another /start.
        if (shouldShowChargingScreen(state.live)) return;

        // Payment guard: if payment is required and missing, the guard will
        // redirect to AMOUNT_PICKER on the next tick. Don't fire /start in
        // that window — the redirect is the right outcome.
        const paymentRequired =
            state.vehicle?.paymentRequired === true ||
            state.session?.vehicle?.paymentRequired === true;
        const hasAuthorizationId = !!state.payment?.authorizationId;
        if (PAYMENTS_ENABLED && paymentRequired && !hasAuthorizationId) return;

        triggerStartCharging();
    }, [
        state.sessionUid,
        state.live?.lifecycleState,
        state.live?.hasReceivedMeterValues,
        state.payment?.authorizationId,
        state.vehicle?.paymentRequired,
        state.session?.vehicle?.paymentRequired,
        state.live,
        failureState,
        triggerStartCharging,
    ]);

    // Unmount cleanup — cancel any live timers so a rehydration back to
    // a different step never leaks work.
    useEffect(() => {
        return () => {
            stopAllTimers();
        };
    }, [stopAllTimers]);

    // Failure-view actions ---------------------------------------------------

    const handleStartAgain = useCallback(() => {
        // Deliberately drops the failed/voided session UID: reset() clears
        // local state (sessionUid, live, payment, vehicle trust) and
        // navigation to /charge lets the FindChargerPage create a fresh
        // session via the normal path.
        stopAllTimers();
        try {
            const uid = state.sessionUid;
            if (uid) clearStartFlag(uid);
        } catch { /* ignore */ }
        reset();
        navigate('/charge');
    }, [reset, navigate, stopAllTimers, state.sessionUid]);

    // "Start again" from the waiting view abandons an attempt the server still
    // holds (the start was sent, the charger never answered), so it is
    // cancelled there first -- unless charging has begun after all, in which
    // case the driver stays here. The terminal view's "Start again" needs no
    // cancel: the server has already ended that session.
    const handleAbandonAndStartAgain = useCallback(async () => {
        const result = await cancelNow();
        if (result?.blockReason === 'charging_started') {
            reportResult(result);
            return;
        }
        handleStartAgain();
    }, [cancelNow, reportResult, handleStartAgain]);

    const handleCheckAgain = useCallback(async () => {
        // One-shot fresh backend probe. Does NOT re-issue /start — the
        // task explicitly forbids automatic retries. If the backend has
        // moved on we honour that; otherwise we stay on the waiting view.
        const uid = state.sessionUid;
        if (!uid || isChecking) return;
        setIsChecking(true);
        try {
            const probe = await getCustomerSession(uid);
            const backendStatus = probe.session?.status;
            if (isBackendTerminalStatus(backendStatus)) {
                enterTerminalFailure(null);
                return;
            }
            if (isBackendActiveStatus(backendStatus)) {
                hasResolvedRef.current = true;
                stopAllTimers();
                setStep(STEPS.MONITOR_CHARGING);
                return;
            }
            // Non-terminal, non-active → charger still hasn't answered.
            showToast({
                message: 'The charger has not responded yet.',
                type: 'info',
            });
        } catch (err) {
            showToast({
                message: 'Could not check the charger. Please try again.',
                type: 'error',
            });
            console.warn('[ConnectCharger] Check again probe failed:', err?.message);
        } finally {
            setIsChecking(false);
        }
    }, [state.sessionUid, isChecking, showToast, enterTerminalFailure, stopAllTimers, setStep]);

    // Rendering -------------------------------------------------------------

    // Defensive guard: if we ever mount without a sessionUid, refuse to
    // render the indefinite plug-in view and refuse to run any of the
    // effects above (they've already early-returned on !sessionUid).
    // Give the user a bounded escape hatch that navigates them back to
    // the setup step so a fresh session can be created. This branch
    // should be UNREACHABLE in practice — VehicleVerification's
    // ensureCustomerSession is the primary boundary — but is kept as
    // defense in depth so a future refactor cannot silently regress.
    if (!hasSessionUid) {
        return (
            <StatusScreen
                footer={(
                    <ActionButtonWrapper
                        actions={[
                            { id: 'return-to-setup', label: 'Return to setup', type: 'btn-dark' },
                        ]}
                        onClickAction={() => setStep(STEPS.CHARGE_SETUP)}
                    />
                )}
            >
                <div className="connect-charger-failure" role="alert" data-testid="cc-no-session-view">
                    <h1 className="connect-charger-failure-title">
                        We couldn't start this charging session
                    </h1>
                    <p className="connect-charger-failure-body">
                        Something went wrong before we could create your session.
                        Please return to setup and try again.
                    </p>
                </div>
            </StatusScreen>
        );
    }

    if (failureState?.kind === 'terminal') {
        return (
            <StatusScreen
                footer={(
                    <ActionButtonWrapper
                        actions={[
                            { id: 'start-again', label: 'Start again', type: 'btn-dark' },
                        ]}
                        onClickAction={handleStartAgain}
                    />
                )}
            >
                <div className="connect-charger-failure" role="alert" data-testid="cc-terminal-view">
                    <h1 className="connect-charger-failure-title">
                        We couldn't start this charging session
                    </h1>
                    <p className="connect-charger-failure-body">
                        {failureState.message
                            || 'The charger did not accept the start request. Please return and try again.'}
                    </p>
                </div>
            </StatusScreen>
        );
    }

    if (failureState?.kind === 'waiting') {
        return (
            <StatusScreen
                footer={(
                    <ActionButtonWrapper
                        actions={[
                            {
                                id: 'check-again',
                                label: isChecking ? 'Checking…' : 'Check again',
                                type: 'btn-outline-dark',
                                disabled: isChecking,
                            },
                            { id: 'start-again', label: 'Start again', type: 'btn-dark' },
                        ]}
                        onClickAction={(actionId) => {
                            if (actionId === 'check-again') return handleCheckAgain();
                            if (actionId === 'start-again') return handleAbandonAndStartAgain();
                        }}
                    />
                )}
            >
                <div className="connect-charger-failure" role="status" data-testid="cc-waiting-view">
                    <h1 className="connect-charger-failure-title">Charging has not started</h1>
                    <p className="connect-charger-failure-body">
                        Check that the cable is connected securely. You can return
                        and try again if the charger does not respond.
                    </p>
                </div>
            </StatusScreen>
        );
    }

    // onClick: ActionButtonWrapper would otherwise take 'cancel' as its own
    // local-only reset and leave the attempt (and any card hold) on the server.
    const actions = [
        { id: 'cancel', label: 'Cancel', type: 'btn-outline-dark', onClick: confirmAndCancel },
    ];

    // Note: The auto-navigation to MONITOR_CHARGING when "Preparing" status is detected
    // is handled by the usePreparingNotification hook in App.jsx, not here.
    // This keeps the logic centralized and avoids duplicate listeners.

    return (
        <StatusScreen
            footer={<ActionButtonWrapper actions={actions} />}
        >
            <StatusView status={STATUS.PLUG_IN} />
        </StatusScreen>
    );
};

export default ConnectCharger;
